import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RcaThunkArg } from "../../actions/actionTypes";
import { ISettingsState } from "../../store/IRcaState";
import { ILanguageInfo } from "../../services/SettingsService";
import * as globalConfigActions from "../../actions/globalConfigActions";
import { ThemeName } from "../../services/ThemeService";

/**
 * Settings data for when the settings dialog is saved
 */
export interface ISettingsData {
  /**
   * The language
   */
  language: string;

  /**
   * Whether the language is right to left
   */
  isRtl: boolean;

  /**
   * Whether auto-fill is enabled
   */
  autoFillEnabled: boolean;

  /**
   * The theme name
   */
  themeName: ThemeName;
}

/**
 * The initial state for the settings slice.
 */
export const initialState: ISettingsState = {
  showDialog: false,
};

/**
 * Saves settings to the server and updates global config.
 */
export const saveSettingsDialog = createAsyncThunk<void, ISettingsData, RcaThunkArg>(
  "settings/saveSettingsDialog",
  async (settings, thunkApi) => {
    const {
      globalConfig: { antiForgeryToken },
    } = thunkApi.getState();
    await thunkApi.extra.settingsService.saveSettingsAsync(
      {
        language: settings.language,
        autoFillEnabled: settings.autoFillEnabled,
        themeName: settings.themeName,
        bannersDismissed: {},
      },
      antiForgeryToken
    );

    thunkApi.dispatch(globalConfigActions.setAutoFillEnabled(settings.autoFillEnabled));
    thunkApi.dispatch(globalConfigActions.setThemeName(settings.themeName));
    await thunkApi.dispatch(globalConfigActions.setLanguage(settings.language, settings.isRtl));
  }
);

/**
 * Loads the list of available languages.
 */
export const loadLanguageList = createAsyncThunk<ILanguageInfo[], string, RcaThunkArg>(
  "settings/loadLanguageList",
  async (language, thunkApi) => thunkApi.extra.settingsService.getLanguagesAsync(language)
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    showSettingsDialog: (state) => {
      state.showDialog = true;
    },
    closeSettingsDialog: (state) => {
      state.showDialog = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveSettingsDialog.fulfilled, (state) => {
      state.showDialog = false;
    });
  },
});

export const { showSettingsDialog, closeSettingsDialog } = settingsSlice.actions;

export default settingsSlice.reducer;
